import React, { useState, useEffect, useContext } from "react";
import { Card, Table, Button, Form, OverlayTrigger, Tooltip} from "react-bootstrap";
import NotyfContext from "../contexts/NotyfContext";
import { X, Check, Edit2, Trash2, Watch, Repeat, Archive, MessageSquare, File} from "react-feather";
import { UserSettingsContext } from "../contexts/UserSettingsContext";
import { useTable, useSortBy, useFilters} from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";

import authenticatedFetch from "../utils/fetch"
import EditableExpires from "./EditableExpires";
import LinkedDocumentsDisplay from "./LinkedDocumentsDisplay";
import LinkedDocumentSelect from "./LinkedDocumentSelect";
import { getDocumentDisplayLabel } from "./linkedDocumentUtils";
import { customExpiresFilter, ExpiresColumnCheckboxFilter, caseInsensitiveSort, getReminderSentDisplay } from "./reactTableUtils";

const BoardCertification = ({ id, providerId, certification, effectiveDate, expirationDate, continuous, notes, archived, reminderSent, documents, refreshList, canEdit, onCancelEdit, startInEditMode=false }) => {
  const [editing, setEditing] = useState(startInEditMode);
  const [localCertification, setLocalCertification] = useState(certification);
  const [localEffectiveDate, setLocalEffectiveDate] = useState(effectiveDate);
  const [localExpirationDate, setLocalExpirationDate] = useState(expirationDate);
  const [localContinuous, setLocalContinuous] = useState(continuous);
  const [localNotes, setLocalNotes] = useState(notes);
  const [localArchived, setLocalArchived] = useState(archived);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [documentFilenames, setDocumentFilenames] = useState([]);

  const { isH3Admin, authorities, documentsRefresh, setDocumentsRefresh } = useContext(UserSettingsContext);

  const notyf = useContext(NotyfContext);

  const setInitialDocumentFilenames = () => {
    let initialDocumentFilenames = []
    let initialSelectedDocuments = []
    if (documents) // undefined if we're excluding the column due to permissions
    {
      for (var i=0; i<documents.length; i++)
      {
        initialDocumentFilenames.push(documents[i].filename);
        initialSelectedDocuments.push({value: documents[i].filename, label: getDocumentDisplayLabel(documents[i])});
      }
    }

    setDocumentFilenames(initialDocumentFilenames);
    setSelectedDocuments(initialSelectedDocuments);
  }

  useEffect(() => {
      setInitialDocumentFilenames();
  }, [id, providerId, documents]);

  useEffect(() => {
    if (!editing) {
      setLocalNotes(notes);
      setLocalEffectiveDate(effectiveDate);
      setLocalExpirationDate(expirationDate);
      setLocalContinuous(continuous);
      setInitialDocumentFilenames();
    }
  }, [effectiveDate, expirationDate, continuous, notes, documents]);

  const handleDocumentChanged = (documents, e) => {
    setSelectedDocuments(documents);

    let newDocumentFilenames = []
    for (var i=0; i<documents.length; i++)
    {
      newDocumentFilenames.push(documents[i].value);
    }

    setDocumentFilenames(newDocumentFilenames);
  }

  function handleDeleteClicked(e) {
    e.preventDefault();
    if (window.confirm("Are you sure you want to delete this item?"))
    {
      authenticatedFetch({
        method: "DELETE",
        path: "boardcertification/" + id,
        successHandler: function(result) {
          refreshList();
          setDocumentsRefresh(documentsRefresh + 1);
          notyf.open({type: "success", message: "Deleted!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
        },
        errorHandler: function(error) {
          notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
        }
      });
    }
  }

  function handleEditClicked(e) {
    e.preventDefault();
    setEditing(true);
  }

  function cancelEdit(e) {
    e.preventDefault();
    setEditing(false);
    setLocalCertification(certification);
    setLocalEffectiveDate(effectiveDate);
    setLocalExpirationDate(expirationDate);
    setLocalContinuous(continuous);
    setLocalNotes(notes);
    setLocalArchived(archived);
    setInitialDocumentFilenames();

    if (onCancelEdit)
    {
      onCancelEdit();
    }
  }

  function saveEdit(e) {
    e.preventDefault();
    if (localCertification === '' && localEffectiveDate === null)
    {
      alert("Please enter a certification or effective date");
      return;
    }

    setEditing(false);

    authenticatedFetch({
      method: id ? "PUT" : "POST",
      requestData: {providerId: parseInt(providerId), certification: localCertification, effectiveDate: localEffectiveDate, expirationDate: localExpirationDate, continuous: localContinuous, notes: localNotes, archived: localArchived, documentFilenames: documentFilenames},
      path: id ? "boardcertification/" + id  : "boardcertification",
      successHandler: function(result) {
        refreshList();
        setDocumentsRefresh(documentsRefresh + 1);
        notyf.open({type: "success", message: id ? "Updated!" : "Saved!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
      },
      errorHandler: function() {
        setEditing(true);
        setLocalCertification(certification);
        setLocalEffectiveDate(effectiveDate);
        setLocalExpirationDate(expirationDate);
        setLocalContinuous(continuous);
        setLocalNotes(notes);
        setLocalArchived(archived);
        setInitialDocumentFilenames();
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    }); 
  }


  function handleCertificationChanged(e) {
    e.preventDefault();
    setLocalCertification(e.target.value);
  }

  function handleEffectiveDateChanged(val, e) {
    e.preventDefault();
    setLocalEffectiveDate(val);
  }

  function handleExpirationDateChanged(val, e) {
    e.preventDefault();
    setLocalExpirationDate(val);
  }

  function handleContinuousChanged(e) {
    setLocalContinuous(e.target.checked);
    if (e.target.checked)
    {
      setLocalExpirationDate(null);
    }
  }

  function handleNotesChanged(e) {
    e.preventDefault();
    setLocalNotes(e.target.value);
  }

  function handleArchivedChanged(e) {
    setLocalArchived(e.target.checked);
  }

  var notesReadOnly = '';
  if (localNotes && localNotes.length > 0) {
    notesReadOnly = localNotes.split("\n").map(function(item, idx) {
      return ( 
          <span key={idx}>
              {item}
              <br />
          </span>
       )
    });
  }

  return (
    <>
    {editing ?
    <td>
      <Form.Control size="sm" autoFocus={true} maxLength={255} value={localCertification} onChange={(e) => {handleCertificationChanged(e)}} />
    </td>
    :
    <td style={
      !localCertification || localCertification.length === 0
        ? {
            backgroundColor: "rgba(220,53,69,0.3)",
            // cancel the striped bg for this cell
            ["--bs-table-accent-bg"]: "transparent"
          }
        : {}
    }>
      {localCertification}
    </td>
    }
    <td>
    <EditableExpires warnWithColors={false} localExpires={localEffectiveDate} localActive={true} editingExpires={editing} handleExpiresChanged={handleEffectiveDateChanged} blankText=""/>
    </td>
    <td style={
      (!localExpirationDate || localExpirationDate.length === 0) && !localContinuous
        ? {
            backgroundColor: "rgba(220,53,69,0.3)",
            // cancel the striped bg for this cell
            ["--bs-table-accent-bg"]: "transparent"
          }
        : {}
    }>
    <EditableExpires localExpires={localExpirationDate} localActive={!localContinuous} editingExpires={editing} handleExpiresChanged={handleExpirationDateChanged} blankText=""/>
    </td>
    {editing ?
    <td>
      <Form.Check type="checkbox" checked={!!localContinuous} onChange={(e) => {handleContinuousChanged(e)}} />
    </td>
    :
    <td>
      {localContinuous ?
        <OverlayTrigger placement="top" overlay={<Tooltip>Continuous</Tooltip>}>
          <span><Repeat style={{'height': '16px', 'width': '16px'}}/></span>
        </OverlayTrigger>
        : <>&nbsp;</>}
    </td>
    }
    {editing ?
    <td>
      <Form.Control size="sm" maxLength={255} value={localNotes} onChange={(e) => {handleNotesChanged(e)}} />
    </td>
    :
    <td>
      {notesReadOnly}
    </td>
    }
    {editing?
    <td> 
      <Form.Check type="checkbox" checked={localArchived} onChange={(e) => {handleArchivedChanged(e)}} />
    </td>
    :
    <td>
      {localArchived ? <Watch style={{'height': '16px', 'width': '16px'}}/> : <>&nbsp;</>}
    </td>
    }
    {(isH3Admin || (authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")))&&
    <>
      {editing && (isH3Admin || authorities.includes("PROVIDER_DOCUMENT_EDIT"))?
      <td>
        <LinkedDocumentSelect entityType={"boardcertification"} providerId={providerId} handleDocumentChanged={handleDocumentChanged} selectedDocuments={selectedDocuments}/>
      </td>
        : 
      <td>
        <LinkedDocumentsDisplay entityType={"boardcertification"} documents={documents} entityId={id}/>
      </td> 
      }
    </>
    }
    <td>{getReminderSentDisplay(reminderSent)}</td>
    <td style={{width: '70px'}}>
      { canEdit?
        <>
        {editing ? 
        <>
          <X style={{'height': '16px', 'width': '16px'}} cursor="pointer" onClick={(e) => {cancelEdit(e)}} />
          &nbsp;<Check style={{'height': '16px', 'width': '16px'}} cursor="pointer" onClick={(e) => {saveEdit(e)}} className="text-success"/>
        </>
        :
        <>
          <Edit2 style={{'height': '12px', 'width': '12px'}} cursor="pointer" onClick={(e) => {handleEditClicked(e)}}/>&nbsp;&nbsp;
          <Trash2 style={{'height': '12px', 'width': '12px'}} cursor="pointer" onClick={(e) => {handleDeleteClicked(e)}}/>
        </>
        }
        </>
        : <>&nbsp;</>
      }
    </td>
  </>
  )
}


const BoardCertificationsTable = ({providerId, addingNew, onCancelAddNew, list, isLoaded, error, canEdit, refreshList}) => {
  const data = list;

  const { isH3Admin, authorities } = useContext(UserSettingsContext);

  const columns = React.useMemo(() => {
    const baseColumns = [
      {
        accessor: "id",
        key: "id",
      },
      {
        Header: "Board Name & Cert.",
        accessor: "certification",
        key: "certification",
      },
      {
        Header: "Effective Date",
        accessor: "effectiveDate",
        key: "effectiveDate",
      },
      {
        Header: "Expiration",
        accessor: "expirationDate",
        key: "expirationDate",
        Filter: ExpiresColumnCheckboxFilter,
        filter: customExpiresFilter("expirationDate")
      },
      {
        Header: "Continuous / MOC",
        accessor: "continuous",
        key: "continuous",
      },
      {
        Header: "Certification #",
        accessor: "notes",
        key: "notes",
      },
      {
        Header: "Not Renewed",
        accessor: "archived",
        key: "archived",
      }
    ];

    if(isH3Admin || (authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")))
    {
      baseColumns.push(
      {
        Header: () => (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Documents</Tooltip>}
          >
            <span>
              <File size={16} />
            </span>
          </OverlayTrigger>
        ),
        accessor: "documents",
        key: "documents",
      });
    }

    baseColumns.push(
      {
        Header: () => (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Reminder Sent</Tooltip>}
          >
            <span>
              <MessageSquare size={16} />
            </span>
          </OverlayTrigger>
        ),
        accessor: "reminderSent",
        key: "reminderSent",
      },
      {
        Header: "",
        accessor: "icons",
        key: "icons",
      },
    );
    return baseColumns;
  }, [isH3Admin, authorities]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
  useTable(
    {
      columns,
      data,
      autoResetSortBy: false,
      autoResetFilters: false,
      initialState: {
        hiddenColumns: ['id'],
        sortBy: [
            {
                id: 'effectiveDate',
                desc: true
            },
            {
              id: 'expirationDate',
              desc: true
            }
        ],
        filters: [
          {
              id: 'expirationDate',
              value: 'excludeGreaterThan30Days',
          },
        ],
      },
      sortTypes: {
        alphanumeric: (row1, row2, columnName) => caseInsensitiveSort({row1, row2, columnName})
      }
    },
    useFilters,
    useSortBy,
  );
  
  // Extract the expires filter
  var expiresFilter = "";
  for (var i=0; i < headerGroups.length; i++)
  {
    for (var j=0; j < headerGroups[i].headers.length; j++)
    {
      var column = headerGroups[i].headers[j];
      if (column.id === "expirationDate")
      {
        expiresFilter = column.render("Filter");
        break;
      }
    }
  }

  return (
    <>
      <Table className="table-sm" striped bordered hover {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()} style={{whiteSpace: "nowrap"}}>
            {headerGroup.headers.map((column) => {
              const { key, ...restHeaderProps } = (column.id === "icons" || column.id === "notes" || column.id === "reminderSent" || column.id === "documents") ? column.getHeaderProps() : column.getHeaderProps(column.getSortByToggleProps());
              return (
                <th {...restHeaderProps} key={key} style={(column.id === "archived" || column.id === "continuous") ? {...restHeaderProps.style, whiteSpace: "normal"} : restHeaderProps.style}>
                  {column.render("Header")}
                  {(column.id !== "icons" && column.id !== "notes" && column.id !== "reminderSent" && column.id !== "documents") ? (
                    <>
                    {column.isSorted ? (
                        column.isSortedDesc ? (
                          <FontAwesomeIcon icon={faSortDown} className="ms-2" />
                        ) : (
                          <FontAwesomeIcon icon={faSortUp} className="ms-2" />
                        )
                      ) : (
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                    )}
                    </>
                  ) : <></>
                  }
                </th>
              )
            }
            )}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {
        error ? (<tr><td colSpan={9}>Something went wrong; please reload the page...</td></tr>) : ( 
          !isLoaded ? (<tr><td colSpan={9}>Loading...</td></tr>) : (
            rows.map((row) => {
            prepareRow(row);
            const { key, ...restRowProps } = row.getRowProps();
            return (
              <tr {...restRowProps} key={key}
                style={{
                  ...(row.values.archived ? {opacity: 0.5} : {}),
                  ...(row.values.notes && row.values.notes.toLowerCase().indexOf("please verify") >= 0
                    ? {
                        // td's read these from the tr; setting them here colors the whole row
                        ["--bs-table-bg"]: "rgba(255,193,7,0.3)",
                        // cancel the striped overlay so it doesn't stack on the warning color
                        ["--bs-table-striped-bg"]: "transparent",
                        ["--bs-table-accent-bg"]: "transparent"
                      }
                    : {})
                }}>
                <BoardCertification providerId={providerId} id={row.values.id} key={row.values.id} certification={row.values.certification ? row.values.certification : ""} effectiveDate={row.values.effectiveDate} expirationDate={row.values.expirationDate} continuous={row.values.continuous == null ? false : row.values.continuous} notes={row.values.notes ? row.values.notes : ""} archived={row.values.archived == null ? false : row.values.archived} reminderSent={row.values.reminderSent} documents={row.values.documents} refreshList={refreshList} canEdit={canEdit}/>
              </tr>
            );
          })))
        }
        {
        addingNew ?
          <tr><BoardCertification providerId={providerId} key={0} startInEditMode={true} certification={""} effectiveDate={null} expirationDate={null} continuous={false} notes={""} archived={false} reminderSent={""} documents={[]} onCancelEdit={onCancelAddNew} canEdit={canEdit} refreshList={refreshList}/></tr>
        : <></>
        }
      </tbody>
    </Table>
    {expiresFilter}
  </>
  )
}

const BoardCertifications = ({providerId, canEdit}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [addingNew, setAddingNew] = useState(false);

  const { documentsRefresh, tasksRefresh } = useContext(UserSettingsContext);

  useEffect(() => {
    refreshList();
  }, [providerId, documentsRefresh, tasksRefresh])

  function refreshList()
  {
    setAddingNew(false);
    authenticatedFetch({
      path: "boardcertification/provider/" + providerId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function() {
        setIsLoaded(true);
        setError(true);
      }
    });
  }

  const handleAddNewClicked = (e) => {
    e.preventDefault();
  
    setAddingNew(true);
  }

  const onCancelAddNew = () => {
    setAddingNew(false);
  }

  var list = [];
  if (isLoaded)
  {
    list = data.map((item, i) => {
      return {key: item.id, id: item.id, certification: item.certification, effectiveDate: item.effectiveDate, expirationDate: item.expirationDate, continuous: item.continuous, notes: item.notes,  archived: item.archived, reminderSent: item.reminderSent, documents: item.documents, updated: item.updated, updatedBy: item.updatedBy}
    }, this);
  }

  return (
    <Card>
      <Card.Header style={{marginBottom: '-20px'}}>
        <Card.Title>Board Certifications
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <BoardCertificationsTable list={list} addingNew={addingNew} onCancelAddNew={onCancelAddNew} providerId={providerId} error={error} isLoaded={isLoaded} canEdit={canEdit} refreshList={refreshList}/>
        {canEdit?
          <span className="float-end" style={{marginTop: '6px'}}>
            <Button size="sm" variant="primary" className="float-end" onClick={(e) => {handleAddNewClicked(e)}}>+ Add New Board Certification</Button>
          </span>
          : <></>
        }
      </Card.Body>
    </Card>
  )
}

export default BoardCertifications;
